import React, {
	useMemo,
	useEffect,
	useState,
	useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ColorResult } from 'react-color';
import classNames from 'classnames';
import Socket from '../../connection/socket';
import { GreenroomState } from '../../stores/types/types';
import { MediaItem } from '../../stores/reducers/media';
import { SelectGroupOption } from '../general-components/select-group';
import { saveMediaItem } from '../../stores/actions/media';
import { isVideo } from '../../utils/utils';
import Icon, { ICONS, COLORS } from '../general-components/icon';
import SelectSmall from '../general-components/select-small';
import Switch from '../general-components/switch';
import ColorPicker from '../right-panel/settings/color-picker';
import Colors from '../../utils/colors';
import InfoBadge, { InfoBadgeMsg } from '../general-components/tooltips/info-badge';

const fitFillOptions: SelectGroupOption[] = [
	{
		label: 'Fit',
		value: false,
	},
	{
		label: 'Fill',
		value: true,
	},
];

interface FullColor {
	hex: string
}

interface Props {
	socket: Socket | null;
	selectedMedia: MediaItem | undefined;
	arrayOfImageUrls: string[];
}

export default function MediaModalButtons(props: Props) {
	const {
		socket,
		selectedMedia,
		arrayOfImageUrls,
	} = props;

	const user = useSelector((state: GreenroomState) => state.SessionReducer.user);
	const token = useSelector((state: GreenroomState) => state.SessionReducer.token);
	const host = useSelector((state: GreenroomState) => state.SessionReducer.host);

	const mediaSettingsDebounce = useRef<NodeJS.Timeout | null>(null);

	const [fill, setFill] = useState<boolean>(false);
	const [backgroundColor, setBackgroundColor] = useState<FullColor>(
		{ hex: Colors.initialBackgroundColor },
	);
	const [loop, setLoop] = useState<boolean>(false);
	const [showColorPicker, setShowColorPicker] = useState<boolean>(false);

	const dispatch = useDispatch();

	useEffect(() => {
		if (selectedMedia?.media_data.fill !== undefined) {
			setFill(selectedMedia.media_data.fill);
		}

		if (selectedMedia?.media_data.background_color) {
			setBackgroundColor({ hex: selectedMedia.media_data.background_color });
		}

		if (selectedMedia?.media_data.loop !== undefined) { setLoop(selectedMedia.media_data.loop); }
	}, [selectedMedia]);

	const handleMediaSettings = (type: string, value: boolean | string) => {
		if (!selectedMedia || !token || !user) return;

		if (mediaSettingsDebounce.current) { clearTimeout(mediaSettingsDebounce.current); }

		mediaSettingsDebounce.current = setTimeout(() => {
			const newMediaData = JSON.parse(JSON.stringify(selectedMedia.media_data));
			newMediaData[type] = value;

			dispatch(saveMediaItem(
				token,
				{
					...selectedMedia,
					media_data: newMediaData,
					edited_by: user.gr_user,
				},
				undefined,
			));
			socket?.setMedia({
				...selectedMedia,
				media_data: newMediaData,
				edited_by: user.gr_user,
			});
		}, 100);
	};

	const deckHasVideos = useMemo(
		() => arrayOfImageUrls.some((url: string) => isVideo(url)),
		[arrayOfImageUrls],
	);

	return (
		<div className="media-modal-bottom">
			{host && (
				<div className="flex background-darker padding-small border-radius-100">
					<SelectSmall
						options={fitFillOptions}
						onChange={(value) => {
							setFill(value);
							handleMediaSettings('fill', value);
						}}
						label={'Fit'}
						selected={fitFillOptions.find((option) => option.value === fill) || fitFillOptions[0]}
						dropdown={true}
						className={'fit-fill-dropdown'}
						optionHeight={32}
					/>
					<div
						className="flex-align-center background-gray-hover padding-button-small margin-right-12 border-radius-100 cursor-pointer line-height-16"
						onClick={() => setShowColorPicker(!showColorPicker)}
					>
						<span className="text-white font-weight-heavy margin-right-small">Background</span>
						<Icon name={ICONS.KEYBOARD_ARROW_DOWN} color={COLORS.WHITE} size={12} />
					</div>
					<ColorPicker
						value={backgroundColor.hex}
						color={backgroundColor}
						label={'Background Color'}
						open={showColorPicker}
						close={() => setShowColorPicker(false)}
						onChange={(color: ColorResult) => {
							setBackgroundColor(color);
							handleMediaSettings('background_color', color.hex);
						}}
					/>
					<div className={classNames(
						'flex-align-center line-height-16',
						!deckHasVideos ? 'background-light' : 'background-lighter',
						'padding-button-small',
						'border-radius-100',
					)}>
						<span className={classNames('text-white font-weight-heavy margin-right-small', !deckHasVideos && 'disabled')}>Loop</span>
						<InfoBadge message={InfoBadgeMsg.LoopToggle}/>
						<Switch
							on={loop}
							onChange={() => {
								setLoop(!loop);
								handleMediaSettings('loop', !loop);
							}}
							className={classNames('green margin-left-small', !deckHasVideos && 'disabled')}
						/>
					</div>
				</div>
			)}
		</div>
	);
}
